export const CONVERSATION_PLACEMENT_PENDING_TTL_MS = 1;

export const CONVERSATION_PLACEMENT_OPERATION_KIND = {
  ARCHIVE: 'archive',
  FOLDER: 'folder',
  STATUS: 'status',
  TRASH: 'trash',
};

export const CONVERSATION_PLACEMENT_SOURCE = {
  API: 'placement-api',
  CACHE: 'placement-cache',
  FETCH: 'placement-fetch',
  LOCAL: 'placement-local',
  REALTIME: 'placement-realtime',
  ROLLBACK: 'placement-rollback',
};

export const normalizeConversationPlacementPage = (page = 1) => {
  const numericPage = Number(page);
  return Number.isFinite(numericPage) && numericPage > 0
    ? Math.floor(numericPage)
    : 1;
};

export const shouldPreserveConversationPageOrder = ({ currentPage = 1 } = {}) =>
  normalizeConversationPlacementPage(currentPage) > 1;

// Sort "più recente in alto": nuova attività realtime appartiene legittimamente
// alla cima della lista anche quando l'ordine delle pagine caricate è congelato.
const LATEST_FIRST_SORT_KEYS = new Set([
  'last_activity_at_desc',
  'last_incoming_email_desc',
  'last_message_at_desc',
  'last_outgoing_email_desc',
]);

// Sort delle viste chat (WhatsApp & co.): la lista segue l'ultimo messaggio
// reale in ingresso O in uscita (conversations.last_message_id lato server).
export const isChatLatestFirstSortKey = (sortKey = null) =>
  sortKey === 'last_message_at_desc';

const resolveEffectiveLatestFirstSortKey = (sortKey = null) =>
  typeof sortKey === 'string' && sortKey ? sortKey : 'last_activity_at_desc';

// Decide se un messaggio realtime deve far salire la conversazione in testa
// mentre l'ordine delle pagine >1 è preservato (il getter non riordina più).
// Incoming fa salire con ogni sort latest-first tranne la cartella Inviata;
// outgoing con il sort chat, dove anche le risposte contano, e nella Inviata,
// dove contano solo le mail mandate.
export const shouldFloatConversationOnRealtimeMessage = ({
  chatSortFilter = null,
  currentPage = 1,
  message = {},
} = {}) => {
  if (!shouldPreserveConversationPageOrder({ currentPage })) return false;
  if (message?.private) return false;

  const sortKey = resolveEffectiveLatestFirstSortKey(chatSortFilter);
  if (!LATEST_FIRST_SORT_KEYS.has(sortKey)) return false;

  const messageType = Number(message?.message_type);
  const isSentFolderSort = sortKey === 'last_outgoing_email_desc';
  if (messageType === 0) return !isSentFolderSort;
  return (
    messageType === 1 && (isChatLatestFirstSortKey(sortKey) || isSentFolderSort)
  );
};

export const shouldSuppressRealtimeConversationPlacementForPage = ({
  chatSortFilter = null,
  currentConversation = null,
  currentPage = 1,
  source = CONVERSATION_PLACEMENT_SOURCE.FETCH,
} = {}) =>
  !currentConversation &&
  source === CONVERSATION_PLACEMENT_SOURCE.REALTIME &&
  shouldPreserveConversationPageOrder({ currentPage }) &&
  !isChatLatestFirstSortKey(chatSortFilter);

const resolveNowMs = (nowMs = () => Date.now()) => {
  const value = typeof nowMs === 'function' ? nowMs() : nowMs;
  return Number.isFinite(Number(value)) ? Number(value) : Date.now();
};

const hasConversationId = (id = null) =>
  id !== null && id !== undefined && id !== '';

const normalizePlacementKey = (value = null) =>
  String(value || '')
    .trim()
    .toUpperCase();

const addPlacementKeys = (keys, values = []) => {
  values.forEach((value) => {
    const normalizedValue = normalizePlacementKey(value);
    if (normalizedValue) {
      keys.add(normalizedValue);
    }
  });
};

const addLabelPlacementKeys = (keys, label = null) => {
  if (!label) return;
  if (typeof label === 'string' || typeof label === 'number') {
    addPlacementKeys(keys, [label]);
    return;
  }
  if (typeof label !== 'object') return;

  addPlacementKeys(keys, [
    label.id,
    label.source_id,
    label.sourceId,
    label.title,
    label.name,
  ]);
};

const getConversationPlacementKeys = (conversation = null) => {
  const keys = new Set();
  if (!conversation || typeof conversation !== 'object') return keys;

  addPlacementKeys(keys, [
    conversation.status,
    conversation.current_status,
    conversation.folder_id,
    conversation.folderId,
    conversation.folder_title,
    conversation.folderTitle,
    conversation.folder_name,
    conversation.folderName,
    conversation.provider_folder_id,
    conversation.providerFolderId,
    conversation.provider_folder_title,
    conversation.providerFolderTitle,
  ]);

  if (Array.isArray(conversation.label_ids)) {
    conversation.label_ids.forEach((labelId) =>
      addPlacementKeys(keys, [labelId])
    );
  }
  if (Array.isArray(conversation.labels)) {
    conversation.labels.forEach((label) => addLabelPlacementKeys(keys, label));
  }

  return keys;
};

const getOperationDestinationKeys = (operation = null) => {
  const keys = new Set();
  if (!operation || typeof operation !== 'object') return keys;

  addPlacementKeys(keys, [
    operation.status,
    operation.targetFolderId,
    operation.targetFolderTitle,
    operation.targetLabelId,
    operation.targetLabelTitle,
  ]);

  return keys;
};

const doesOperationMatchIncomingPlacement = ({
  incomingConversation = null,
  operation = null,
} = {}) => {
  const destinationKeys = getOperationDestinationKeys(operation);
  if (!destinationKeys.size) return false;

  const conversationKeys = getConversationPlacementKeys(incomingConversation);
  if (!conversationKeys.size) return false;

  return [...destinationKeys].some((key) => conversationKeys.has(key));
};

export const buildConversationPlacementOperation = ({
  id = null,
  inboxId = null,
  kind = CONVERSATION_PLACEMENT_OPERATION_KIND.FOLDER,
  nowMs = () => Date.now(),
  removeFromList = true,
  status = null,
  targetFolderId = null,
  targetFolderTitle = null,
  targetLabelId = null,
  targetLabelTitle = null,
  ttlMs = CONVERSATION_PLACEMENT_PENDING_TTL_MS,
} = {}) => {
  if (!hasConversationId(id)) return null;

  const startedAt = resolveNowMs(nowMs);
  return {
    id: String(id),
    inboxId,
    kind,
    removeFromList: Boolean(removeFromList),
    startedAt,
    status,
    targetFolderId,
    targetFolderTitle,
    targetLabelId,
    targetLabelTitle,
    expiresAt: startedAt + ttlMs,
  };
};

export const isConversationPlacementOperationActive = ({
  nowMs = () => Date.now(),
  operation = null,
} = {}) => {
  if (!operation || !hasConversationId(operation.id)) return false;
  return resolveNowMs(nowMs) <= Number(operation.expiresAt || 0);
};

export const shouldSuppressConversationPlacementUpdate = ({
  incomingConversation = null,
  nowMs = () => Date.now(),
  operation = null,
  source = CONVERSATION_PLACEMENT_SOURCE.FETCH,
} = {}) => {
  if (
    !isConversationPlacementOperationActive({ nowMs, operation }) ||
    !operation.removeFromList
  ) {
    return false;
  }

  if (
    source !== CONVERSATION_PLACEMENT_SOURCE.ROLLBACK &&
    doesOperationMatchIncomingPlacement({ incomingConversation, operation })
  ) {
    return false;
  }

  return source !== CONVERSATION_PLACEMENT_SOURCE.ROLLBACK;
};

export const resolveConversationPlacementUpdate = ({
  chatSortFilter = null,
  currentConversation = null,
  currentPage = 1,
  incomingConversation = null,
  nowMs = () => Date.now(),
  pendingOperation = null,
  source = CONVERSATION_PLACEMENT_SOURCE.FETCH,
} = {}) => {
  if (
    shouldSuppressRealtimeConversationPlacementForPage({
      chatSortFilter,
      currentConversation,
      currentPage,
      source,
    })
  ) {
    return {
      conversation: currentConversation,
      shouldApply: false,
      status: 'suppressed-page',
    };
  }

  if (
    shouldSuppressConversationPlacementUpdate({
      incomingConversation,
      nowMs,
      operation: pendingOperation,
      source,
    })
  ) {
    return {
      conversation: currentConversation,
      shouldApply: false,
      status: 'suppressed',
    };
  }

  return {
    conversation: incomingConversation,
    shouldApply: true,
    status: 'applied',
  };
};

export const filterPendingPlacementConversations = ({
  conversations = [],
  nowMs = () => Date.now(),
  pendingOperations = {},
  source = CONVERSATION_PLACEMENT_SOURCE.FETCH,
} = {}) => {
  if (!Array.isArray(conversations)) return [];

  return conversations.filter((conversation) => {
    const operation = pendingOperations?.[String(conversation?.id)];
    return !shouldSuppressConversationPlacementUpdate({
      incomingConversation: conversation,
      nowMs,
      operation,
      source,
    });
  });
};
